import { DOCUMENT } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component,
  DestroyRef,
  effect,
  inject,
  input,
  InputSignal,
  isDevMode,
  OnInit,
  Signal,
  signal,
  viewChild,
  WritableSignal,
  ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AbstractControl, FormControl, FormGroup, FormsModule, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { Router } from '@angular/router';
import { PromptService, ToastMessageService } from '@k12/app-services';
import { LoadingSpinnerService } from '@k12/loading';
import {
  ComponentType,
  findDiscriminatorInput,
  InputDataType,
  NextPrompt,
  ProgramReview,
  ProgramTypes,
  Prompt,
  PromptComponent,
  PromptInput,
  PromptInputOption,
  ProviderType,
  SubmitPrompt,
  SubmitPromptResponse,
  ToastMessageType
} from '@k12/models';
import { OrderByPipe } from '@k12/pipes';
import { errorToastValue } from '@k12/utils';
import {
  feinValidator,
  householdSizeValidator,
  numberValidator,
  numericOrDecimalValidator,
  phoneValidator,
  requiredEmailValidator,
  ssnLast4Validator,
  ssnValidator,
  urlValidator,
  zipCodeValidator } from '@k12/validators';
import { combineLatest, startWith } from 'rxjs';
import { PromptRendererComponent } from '../prompt-renderer/prompt-renderer.component';

// RadioFormField components use N independent boolean FormControls (one per option, same
// shape as CheckboxFormField) rather than one shared control, so "must select one option when
// required" can't be expressed with Validators.required/requiredTrue on an individual control --
// it has to be evaluated across the whole group.
export function atLeastOneSelectedValidator(): ValidatorFn {
  return (group: AbstractControl): ValidationErrors | null => {
    const formGroup = group as FormGroup;
    const hasSelection = Object.values(formGroup.controls).some((control) => control.value === true);
    return hasSelection ? null : { atLeastOneRequired: true };
  };
}

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [FormsModule, MatButtonModule, OrderByPipe, PromptRendererComponent],
  selector: 'lib-enrollment-renderer',
  styleUrl: './enrollment-renderer.component.scss',
  templateUrl: './enrollment-renderer.component.html'
})
export class EnrollmentRendererComponent implements OnInit {
  public promptRendererComponent: Signal<PromptRendererComponent | undefined> = viewChild(PromptRendererComponent);

  public formGroups: Record<string, FormGroup> = {};
  public InputDataType: typeof InputDataType = InputDataType;
  public isBackDisabled: WritableSignal<boolean> = signal(true);
  public isPromptValid: WritableSignal<boolean> = signal(false);
  public isReviewPrompt: WritableSignal<boolean> = signal(false);
  public promptData: WritableSignal<Prompt | NextPrompt> = signal({} as unknown as Prompt);
  public reviewComponentData: WritableSignal<ProgramReview> = signal(<ProgramReview>{});
  public sessionId: WritableSignal<string> = signal('');

  public appUrl: InputSignal<string | undefined> = input();
  public isIneligible: InputSignal<boolean> = input(false);
  public initialSessionId: InputSignal<string> = input('');
  public programToken: InputSignal<string> = input('');
  public programType: InputSignal<ProgramTypes> = input.required();

  private readonly destroyRef: DestroyRef = inject(DestroyRef);
  private readonly document: Document = inject(DOCUMENT);
  private readonly promptService: PromptService = inject(PromptService);
  private readonly router: Router = inject(Router);
  private readonly spinnerService: LoadingSpinnerService = inject(LoadingSpinnerService);
  private readonly toastMessageService: ToastMessageService = inject(ToastMessageService);

  /** The post-submit landing route, which reads the session id as its support reference and retry key. */
  private static readonly EnrollmentCompletePath = '/enrollment-complete';

  constructor() {
    effect(() => {
      if (this.promptData()?.components && this.promptData().components?.length > 0) {
        if (this.promptData().parentPromptToken) {
          this.isBackDisabled.set(!this.promptData().parentPromptToken && this.promptData().parentPromptToken! !== '');
        } else {
          this.isBackDisabled.set(this.promptData().parentPromptTokens!.length === 0);
        }
        this.createFormGroup();
      }
    });
  }

  public ngOnInit(): void {
    const existingSessionId = this.initialSessionId();
    if (existingSessionId) {
      this.sessionId.set(existingSessionId);
      this.loadExistingSession(existingSessionId);
      return;
    }
    if (this.programToken()) {
      this.getPromptData();
      return;
    }
    // programToken is no longer input.required() now that sessionId can bootstrap instead, so
    // neither being supplied would otherwise render an empty shell with no indication of why.
    if (isDevMode()) {
      throw new Error('EnrollmentRendererComponent requires either initialSessionId or programToken.');
    }
  }

  public back(): void {
    this.promptService.getPreviousPrompt(this.sessionId()).subscribe((promptResponse: SubmitPromptResponse) => {
      if (promptResponse.prompt) {
        promptResponse.prompt.promptDataDictionary = promptResponse.promptDataDictionary!;
        this.promptData.set(promptResponse.prompt);
      }
    });
  }

  public next(): void {
    const submitPromptData: SubmitPrompt = this.formatSubmitPromptData();

    this.spinnerService.showSpinner();
    this.promptService.submitPrompt(this.sessionId(), submitPromptData).subscribe({
      next: (promptResponse: SubmitPromptResponse) => {
        // Keep the spinner up through getReviewPrompt so the previous interactive prompt cannot
        // flash after submit returns and before the read-only review is ready to render.
        if (this.hasReviewComponent(promptResponse.nextPrompt.components)) {
          this.promptService.getReviewPrompt(this.sessionId()).subscribe({
            next: (reviewResponse) => {
              this.promptRendererComponent()?.resetChildContainers();
              this.promptData.set(promptResponse.nextPrompt);
              this.reviewComponentData.set(reviewResponse);
              this.spinnerService.hideSpinner();
            },
            error: (err: HttpErrorResponse) => {
              this.spinnerService.hideSpinner();
              this.toastMessageService.showToast(ToastMessageType.Error, errorToastValue(err));
            }
          });
        } else {
          this.promptRendererComponent()?.resetChildContainers();
          this.promptData.set(promptResponse.nextPrompt);
          this.spinnerService.hideSpinner();
        }
      },
      error: (error: HttpErrorResponse) => {
        this.spinnerService.hideSpinner();
        this.toastMessageService.showToast(ToastMessageType.Error, errorToastValue(error));
      }
    });
  }

  public submitEnrollment(): void {
    this.spinnerService.showSpinner();
    this.promptService.submitEnrollment(this.sessionId()).subscribe({
      next: () => {
        this.spinnerService.hideSpinner();
        this.navigateAfterSubmit();
      },
      error: (error: HttpErrorResponse) => {
        this.spinnerService.hideSpinner();
        this.toastMessageService.showToast(ToastMessageType.Error, errorToastValue(error));
      }
    });
  }

  /**
   * Router navigation, not a location assign: a full page load re-bootstraps the app, which re-runs
   * TokenValidationService and can call account/assign-new-user-security while the registration saga
   * is still provisioning. It also discards the MSAL in-memory state for no benefit.
   */
  private navigateAfterSubmit(): void {
    const target = this.appUrl();
    if (!target) {
      return;
    }

    const url = new URL(target, this.document.location.origin);

    // Only this component knows the session id -- the wrapper built this URL before the session
    // existed. Set rather than append, so a stale one from the route cannot survive.
    const sessionId = this.sessionId();
    if (sessionId && url.pathname.endsWith(EnrollmentRendererComponent.EnrollmentCompletePath)) {
      url.searchParams.set('sessionId', sessionId);
    }

    if (url.origin !== this.document.location.origin) {
      this.document.location.assign(url.toString());
      return;
    }

    void this.router.navigateByUrl(`${url.pathname}${url.search}${url.hash}`);
  }

  public editPrompt(event: { id: string; token: string }): void {
    this.spinnerService.showSpinner();
    this.promptService.getSessionPromptByToken(event.id, event.token).subscribe({
      next: (response) => {
        this.spinnerService.hideSpinner();
        this.reviewComponentData.set(<ProgramReview>{});
        response.prompt.promptDataDictionary = response.promptDataDictionary!;
        this.promptData.set(response.prompt);
      },
      error: (error: HttpErrorResponse) => {
        this.spinnerService.hideSpinner();
        this.toastMessageService.showToast(ToastMessageType.Error, errorToastValue(error));
      }
    });
  }

  public agreementSelectOutput(event: { formName: string; control: Record<string, unknown> }): void {
    this.formGroups[event.formName].patchValue(event.control);
  }

  // Generic on purpose: CheckboxFormField emits a single-key control dict (one option
  // independently toggled); RadioFormField emits a multi-key control dict covering every
  // sibling option (the clicked one true, the rest false) to enforce mutual exclusion. Both
  // shapes are just "apply every entry in the control dict", so one handler covers both.
  public checkboxSelectOutput(event: { formName: string; control: Record<string, unknown> }): void {
    Object.entries(event.control).forEach(([inputId, value]) => {
      this.formGroups[event.formName]?.controls[inputId]?.setValue(value);
    });
  }

  public dnpeComponentOutput(event: { formName: string; control: Record<string, unknown> }): void {
    this.formGroups[event.formName].patchValue(event.control);
  }

  public decisionComponentOutput(event: { formName: string; control: Record<string, unknown> }): void {
    this.formGroups[event.formName].patchValue(event.control);
  }

  public fileUploadOutput(event: { formName: string; control: Record<string, unknown> }): void {
    this.formGroups[event.formName].patchValue(event.control);
    this.formGroups[event.formName].updateValueAndValidity();
  }

  public buttonSelectOutput(event: {
    sub: PromptInputOption;
    subSelectInputId: string;
    parentId: string;
  }): void {
    const formGroup = this.formGroups[event.parentId];
    const control = formGroup?.controls[event.subSelectInputId];
    if (!control) {
      return;
    }

    const parentInput = this.promptData().components
      .find((component) => component.id === event.parentId)
      ?.inputs?.find((input) => input.id === event.subSelectInputId);
    const isSingleSelect = parentInput?.value === 'therapy';

    if (isSingleSelect) {
      control.setValue(event.sub.selected ? [event.sub.value] : []);
    } else {
      const currentValue: string[] = Array.isArray(control.value) ? control.value : [];
      if (event.sub.selected) {
        control.setValue([...currentValue.filter((item: string) => item !== event.sub.value), event.sub.value]);
      } else {
        control.setValue(currentValue.filter((item: string) => item !== event.sub.value));
      }
    }

    Object.keys(this.formGroups).forEach((key: string) => {
      this.formGroups[key].updateValueAndValidity();
    });
  }

  public selectSubSelectCheckboxOutput(event: { parentId: string; value: PromptInput }): void {
    const formGroup = this.formGroups[event.parentId];
    if (!formGroup || !event.value.id) {
      return;
    }

    const component = this.promptData().components.find((c) => c.id === event.parentId);
    const path = this.subSelectPathControl(component);

    // SelectSubSelect parents are mutually exclusive: clear sibling option controls. The
    // discriminator survives the sweep because it is the branch answer, not a sub-option bucket;
    // the legacy placeholder does not, since it exists only to gate the nothing-selected state.
    Object.keys(formGroup.controls).forEach((controlName) => {
      const keep = controlName === event.value.id || (path.isDiscriminator && controlName === path.id);
      if (!keep) {
        formGroup.removeControl(controlName);
      }
    });

    if (event.value.selected) {
      if (path.isDiscriminator) {
        formGroup.controls[path.id]?.setValue(event.value.value ?? '');
      }
      formGroup.addControl(
        event.value.id,
        new FormControl([], event.value.value !== ProviderType.Transportation ? Validators.required : undefined));
    } else {
      formGroup.removeControl(event.value.id);

      // Emptying the path control is what keeps the form invalid when no path is selected.
      if (path.isDiscriminator) {
        formGroup.controls[path.id]?.setValue('');
      } else {
        formGroup.addControl(path.id, new FormControl([], Validators.required));
      }
      formGroup.markAsPristine();
    }

    formGroup.updateValueAndValidity();
  }

  /**
   * The control holding a SelectSubSelect component's chosen path. Normally the server-owned
   * discriminator input, whose answer the branch conditions evaluate. Legacy components authored
   * before the discriminator existed fall back to a component-keyed placeholder that gates validity
   * but is never submitted -- an answer row keyed by a component id would violate PromptData's
   * input foreign key.
   */
  private subSelectPathControl(component?: PromptComponent): { id: string; isDiscriminator: boolean } {
    const discriminatorId = findDiscriminatorInput(component)?.id;
    return discriminatorId
      ? { id: discriminatorId, isDiscriminator: true }
      : { id: component?.id ?? '', isDiscriminator: false };
  }

  private createFormGroup(): void {
    this.formGroups = {};

    // Components and inputs coming from the enrollment API always carry server-assigned GUIDs;
    // form groups are keyed by component id and controls by input id.
    this.promptData().components.forEach((component: PromptComponent) => {
      const componentId = component.id!;
      this.formGroups[componentId] = new FormGroup({});

      if (component.inputs && component.inputs?.length > 0) {
        // SelectSubSelect sub-option controls are added dynamically when a parent option is checked.
        // Only the path control exists up front; its required validator keeps the form invalid until
        // a path is picked.
        if (component.componentType === ComponentType.SelectSubSelect) {
          const path = this.subSelectPathControl(component);
          this.formGroups[componentId].addControl(
            path.id,
            path.isDiscriminator
              ? new FormControl(this.returnFormInputValue(path.id, '') ?? '', Validators.required)
              : new FormControl([], Validators.required)
          );
          return;
        }

        component.inputs.forEach((input: PromptInput) => {
          const inputId = input.id!;
          const isDateInput = this.isDateInput(input.dataType);
          const storedValue = this.returnFormInputValue(
            inputId,
            component.componentType === ComponentType.TermsAndConditions ? false : ''
          );
          // Datepickers hold Date | null. Keep that shape so MatDatepickerInput and submit agree.
          const storedDateValue = typeof storedValue === 'string' ? storedValue : undefined;
          const previousValue = isDateInput
            ? this.parseDateControlValue(storedDateValue)
            : storedValue;

          this.formGroups[componentId].addControl(inputId, new FormControl(previousValue));

          if (previousValue && this.returnFormInputValue(inputId)) {
            this.formGroups[componentId].controls[inputId].markAsDirty();
          }

          if (input.isRequired) {
            if (
              component.componentType === ComponentType.TermsAndConditions ||
              component.componentType === ComponentType.CheckboxFormField
            ) {
              this.formGroups[componentId].controls[inputId].addValidators(Validators.requiredTrue);
            } else if (component.componentType !== ComponentType.RadioFormField) {
              // RadioFormField: forcing every option's control to true/requiredTrue would make the
              // group unsatisfiable. "Must select one" is enforced once per component below instead.
              this.formGroups[componentId].controls[inputId].addValidators(Validators.required);
            }
          }

          if (input.max) {
            this.formGroups[componentId].controls[inputId].addValidators(Validators.maxLength(Number(input.max)));
          }

          switch (input.dataType) {
            case InputDataType.Email:
              this.formGroups[componentId].controls[inputId].addValidators(requiredEmailValidator());
              break;
            case InputDataType.Phone:
              this.formGroups[componentId].controls[inputId].addValidators(phoneValidator());
              break;
            case InputDataType.Url:
              this.formGroups[componentId].controls[inputId].addValidators(urlValidator());
              break;
            case InputDataType.SSN4:
              this.formGroups[componentId].controls[inputId].addValidators(ssnLast4Validator());
              break;
            case InputDataType.SSN9:
              this.formGroups[componentId].controls[inputId].addValidators(ssnValidator());
              break;
            case InputDataType.ZipCode:
              this.formGroups[componentId].controls[inputId].addValidators(zipCodeValidator());
              break;
            case InputDataType.EIN:
              this.formGroups[componentId].controls[inputId].addValidators(feinValidator());
              break;
            case InputDataType.IdNumber:
              this.formGroups[componentId].controls[inputId].addValidators(numberValidator());
              break;
            case InputDataType.HHSize:
              this.formGroups[componentId].controls[inputId].addValidators(householdSizeValidator());
              break;
            case InputDataType.Number:
              if (input.min) {
                this.formGroups[componentId].controls[inputId].addValidators(Validators.min(Number(input.min)));
              }
              break;
            case InputDataType.HHIncome:
              this.formGroups[componentId].controls[inputId].addValidators(numericOrDecimalValidator());
              break;
          }
        });

        if (component.componentType === ComponentType.RadioFormField && component.inputs.some((i) => i.isRequired)) {
          this.formGroups[componentId].addValidators(atLeastOneSelectedValidator());
          this.formGroups[componentId].updateValueAndValidity();
        }
      }
    });

    // Update the validity of all controls in the form groups to ensure that any changes
    // to validators are reflected in the form's state.
    Object.values(this.formGroups).forEach((formGroup) => {
      Object.values(formGroup.controls).forEach((control) => {
        control.updateValueAndValidity();
      });
    });

    this.subscribeFormGroup();
  }

  private formatSubmitPromptData(): SubmitPrompt {
    const submitPrompt: SubmitPrompt = {
      enrollmentSessionId: this.sessionId(),
      promptToken: this.promptData().token,
      inputValues: []
    };

    for (const formGroup of Object.values(this.formGroups)) {
      for (const [inputId, value] of Object.entries(formGroup.value)) {
        if (Array.isArray(value) && value.length > 0) {
          value.forEach((arrayValue: string) => {
            submitPrompt.inputValues.push({ inputId, value: arrayValue });
          });
        } else if (value instanceof Date && !Number.isNaN(value.getTime())) {
          // mat-datepicker writes a Date. JSON would turn that into an ISO string, but this
          // loop previously kept only string | number | boolean, so Date of Birth never left
          // the client and projection failed with "missing: Date of Birth".
          submitPrompt.inputValues.push({ inputId, value: this.formatDateControlValue(value) });
        } else if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
          submitPrompt.inputValues.push({ inputId, value: value.toString() });
        }
      }
    }
    return submitPrompt;
  }

  private isDateInput(dataType: string | undefined): boolean {
    return dataType === InputDataType.Date
      || dataType === InputDataType.FullDate
      || dataType === InputDataType.ShortDate;
  }

  // Local calendar date, not UTC. toISOString() would shift a US evening pick to the next day.
  private formatDateControlValue(value: Date): string {
    const year = value.getFullYear();
    const month = String(value.getMonth() + 1).padStart(2, '0');
    const day = String(value.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  private parseDateControlValue(raw: string | undefined): Date | null {
    if (!raw?.trim()) {
      return null;
    }

    const isoDate = /^(\d{4})-(\d{2})-(\d{2})/.exec(raw.trim());
    if (isoDate) {
      const parsed = new Date(Number(isoDate[1]), Number(isoDate[2]) - 1, Number(isoDate[3]));
      return Number.isNaN(parsed.getTime()) ? null : parsed;
    }

    const fallback = new Date(raw);
    return Number.isNaN(fallback.getTime()) ? null : fallback;
  }

  private returnFormInputValue(inputId: string, defaultValue?: string | boolean): string | boolean | undefined {
    const promptDataDictionary = (this.promptData() as NextPrompt).promptDataDictionary;
    const value = promptDataDictionary?.[inputId]?.value;

    if (promptDataDictionary && Object.keys(promptDataDictionary).length > 0 && value) {
      return value;
    }

    return defaultValue;
  }

  private getPromptData() {
    this.spinnerService.showSpinner();
    this.promptService.getNewEnrollment(this.programToken()).subscribe({
      next: (response) => {
        this.spinnerService.hideSpinner();
        this.promptData.set(response.nextPrompt);
        this.sessionId.set(response.sessionId);
      },
      error: (error: HttpErrorResponse) => {
        this.spinnerService.hideSpinner();
        this.toastMessageService.showToast(ToastMessageType.Error, errorToastValue(error));
      }
    });
  }

  private loadExistingSession(sessionId: string): void {
    this.spinnerService.showSpinner();
    this.promptService.getEnrollmentSession(sessionId).subscribe({
      next: (response) => {
        this.sessionId.set(response.sessionId);

        // Resuming straight onto the review step needs the aggregated answers too. Only next()
        // used to fetch them, so a refresh or a deep link there rendered an empty review.
        if (this.hasReviewComponent(response.nextPrompt.components)) {
          this.promptService.getReviewPrompt(response.sessionId).subscribe({
            next: (reviewResponse) => {
              this.promptData.set(response.nextPrompt);
              this.reviewComponentData.set(reviewResponse);
              this.spinnerService.hideSpinner();
            },
            error: (error: HttpErrorResponse) => {
              this.spinnerService.hideSpinner();
              this.toastMessageService.showToast(ToastMessageType.Error, errorToastValue(error));
            }
          });
          return;
        }

        this.spinnerService.hideSpinner();
        this.promptData.set(response.nextPrompt);
      },
      error: (error: HttpErrorResponse) => {
        this.spinnerService.hideSpinner();
        this.toastMessageService.showToast(ToastMessageType.Error, errorToastValue(error));
      }
    });
  }

  // A prompt is the review/submit step iff it contains a review component. This is derived from the
  // component type, never the key path: a prompt can be named/keyed "review" (e.g. household/review)
  // while actually carrying a form component, and must not be treated as the terminal review.
  private hasReviewComponent(components: PromptComponent[] | undefined): boolean {
    return components?.some((component) => component.componentType === ComponentType.Review) ?? false;
  }

  private subscribeFormGroup(): void {
    const formGroups = Object.values(this.formGroups);
    const isReview = this.hasReviewComponent(this.promptData().components);
    this.isReviewPrompt.set(isReview);

    // Uniquified components each get their own FormGroup. Optional radios stay pristine when
    // skipped, so requiring every group to be dirty blocked Next. Enable Next when every group
    // is valid (mandatory radios still fail atLeastOneSelected until a choice is made).
    if (formGroups.length === 0) {
      this.isPromptValid.set(isReview);
      return;
    }

    combineLatest(formGroups.map((form) => form.statusChanges.pipe(startWith(form.status))))
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((statuses) => {
        this.isReviewPrompt.set(isReview);
        this.isPromptValid.set(isReview || statuses.every((status) => status === 'VALID'));
      });
  }
}
